/**
 * DemoWaveRunner — Executes scripted game actions during cinematic sequences.
 * Handles enemy spawning, auto robot placement, and wave control
 * without going through the normal WaveManager queue.
 */
import Phaser from 'phaser';
import { Enemy } from '@/entities/Enemy';
import { EnemyType, EnemyGrade, TILE_SIZE } from '@/config/GameConfig';
import { PATHS, SPAWN_POSITIONS } from '@/map/MapData';
import { EventBus } from '@/events/EventBus';
import { CinematicEvents } from '@/cinematic/CinematicEvents';

/** Reference to GameScene managers, resolved at construction via registry */
interface GameRefs {
  scene: Phaser.Scene;
  waveManager: { enemies: Enemy[]; waveActive: boolean; enemiesSpawned: number };
  robotManager: { addRobot: (robot: any, rules: any[]) => void };
}

export class DemoWaveRunner {
  private refs: GameRefs;
  private demoEnemies: Enemy[] = [];

  constructor(gameScene: Phaser.Scene) {
    this.refs = {
      scene: gameScene,
      waveManager: gameScene.registry.get('waveManager'),
      robotManager: gameScene.registry.get('robotManager'),
    };
  }

  /**
   * Execute a scripted action.
   * @param action - One of: spawnEnemies, placeRobot, startWave, destroyEnemy
   * @param params - Action-specific parameters
   */
  execute(action: string, params: Record<string, unknown> = {}): void {
    switch (action) {
      case 'spawnEnemies':  this.spawnEnemies(params);  break;
      case 'placeRobot':    this.placeRobot(params);    break;
      case 'selectPreset':  this.selectPreset(params);  break;
      case 'destroyEnemy':  this.destroyAllDemo();      break;
      default:
        console.warn(`[DemoWaveRunner] Unknown action: ${action}`);
    }
  }

  /** Spawn demo enemies on the first available path */
  private spawnEnemies(params: Record<string, unknown>): void {
    const count = (params.count as number) ?? 3;
    const type = (params.type as EnemyType) ?? EnemyType.GRUNT;
    const grade = (params.grade as EnemyGrade) ?? EnemyGrade.NORMAL;
    const pathIdx = (params.pathIndex as number) ?? 0;

    const path = PATHS[pathIdx] ?? PATHS[0];
    if (!path || path.length === 0) {
      console.warn('[DemoWaveRunner] No path available for spawning');
      return;
    }

    const wm = this.refs.waveManager;
    wm.waveActive = true;

    for (let i = 0; i < count; i++) {
      // Stagger spawn slightly along path start
      const enemy = new Enemy(this.refs.scene, [...path], type, 0, grade);
      enemy.fogImmune = true; // always visible during cinematic demo
      // Offset each enemy start position slightly so they don't stack
      if (SPAWN_POSITIONS.length > 0) {
        const sp = SPAWN_POSITIONS[pathIdx] ?? SPAWN_POSITIONS[0];
        enemy.body.setPosition(sp.x + (i * 12), sp.y + (i * 8));
      }
      wm.enemies.push(enemy);
      // NOTE: Do NOT increment wm.enemiesSpawned — that counter tracks the
      // spawnQueue and incrementing it makes enemiesRemaining go to 0
      // (spawnQueue is empty for demo spawns), triggering instant WAVE_COMPLETED.
      // NOTE: Do NOT emit ENEMY_SPAWNED here — the Enemy constructor already emits it.
      // A double emission causes JuiceEffects pop-in to capture scale=0 as the target,
      // making the sprite permanently invisible.
      this.demoEnemies.push(enemy);
    }
  }

  /** Auto-place a robot at the specified tile col/row using a preset name */
  private placeRobot(params: Record<string, unknown>): void {
    const col = params.col as number;
    const row = params.row as number;
    const presetName = params.preset as string;

    if (col == null || row == null) {
      console.warn('[DemoWaveRunner] placeRobot requires col and row params');
      return;
    }

    // Emit an event that UIScene can listen to for placing the robot
    // This avoids directly coupling to Robot construction (which requires design data)
    EventBus.emit(CinematicEvents.AUTO_PLACE_ROBOT, {
      col, row, presetName,
      worldX: col * TILE_SIZE + TILE_SIZE / 2,
      worldY: row * TILE_SIZE + TILE_SIZE / 2,
    });
  }

  /** Select a preset by name — emits event that UIScene handles to enter placement mode */
  private selectPreset(params: Record<string, unknown>): void {
    const presetName = (params.preset as string) ?? 'Turret';
    EventBus.emit(CinematicEvents.SELECT_PRESET, { presetName });
  }

  /** Destroy all demo-spawned enemies (cleanup) */
  destroyAllDemo(): void {
    // Deactivate wave BEFORE destroying enemies to prevent
    // WaveManager from detecting 0 enemies and triggering WAVE_COMPLETED
    const wm = this.refs.waveManager;
    if (wm) wm.waveActive = false;

    for (const enemy of this.demoEnemies) {
      if (enemy.alive) {
        enemy.destroy();
      }
    }
    this.demoEnemies = [];
  }

  /** Check if all demo enemies are dead */
  areDemoEnemiesDead(): boolean {
    return this.demoEnemies.every(e => !e.alive);
  }

  destroy(): void {
    this.destroyAllDemo();
  }
}
